// "Olvidé mi contraseña" (INC-12c-2, D-291): the public screen where a person
// who forgot their password asks for a link, reached from the sign in of the
// three experiences. Outside every session and outside the Gate, branded by
// bootstrap from the host like the access screen, and built from the same
// pieces; without a tenant, the same neutral state and no form (D-77).
//
// The answer is always the same, whether the address names anybody or not:
// the screen says what happens if it does, and nothing else. The link the mail
// carries opens the screen where an invitation's opens (InvitationScreen).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { CODES, isApiError, isNetworkError } from '../api/errors';
import { brandOf } from '../app/brand';
import { useRuntime, useSession } from '../app/hooks';
import { useTitleSection } from '../app/documentTitle';
import { logUnexpected } from '../app/unexpected';
import { BrandLockup } from '../ui/BrandLockup';

export function RecoveryScreen() {
  const { t } = useTranslation();
  // INC-14a-3a-1: the tab names this screen before the organization, and no product.
  useTitleSection(t('recovery.title'), 'outside');
  const { session } = useRuntime();
  const snapshot = useSession();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const organization = snapshot.bootstrap?.organization ?? null;
  const brand = brandOf(snapshot);
  const unknownTenant = snapshot.bootstrapStatus === 'unknown_tenant';

  // INC-15b-3 (C, point 180): what nobody expected is said in words, and its
  // code goes to the log, never to the screen.
  const unexpected = (failure: unknown): void => {
    logUnexpected('password recovery', failure);
    setError(t('recovery.unexpectedError'));
  };

  const submit = async (event: { preventDefault(): void }): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await session.requestPasswordRecovery(email.trim());
      setSent(true);
    } catch (failure) {
      if (isNetworkError(failure)) setError(t('access.networkError'));
      else if (isApiError(failure)) {
        if (failure.code === CODES.rateLimited) {
          setError(
            t('access.rateLimited', { seconds: Math.ceil((failure.retryAfterMs ?? 0) / 1000) }),
          );
        } else if (failure.code === CODES.organizationSuspended) {
          setError(t('access.organizationSuspended'));
        } else unexpected(failure);
      } else unexpected(failure);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="access"
      data-testid="recovery-screen"
      data-state={unknownTenant ? 'neutral' : 'branded'}
    >
      <BrandLockup
        name={organization !== null ? organization.name : t('access.neutralTitle')}
        logoUrl={brand.logoUrl}
        markUrl={brand.markUrl}
      />
      {unknownTenant ? (
        <div className="alert warn" role="alert" data-testid="unknown-tenant">
          {t('access.unknownTenant')}
        </div>
      ) : sent ? (
        <div className="card stack" data-testid="recovery-sent">
          <p role="status" style={{ margin: 0 }}>
            {t('recovery.sent')}
          </p>
          <Link className="btn secondary block" to="/login">
            {t('invitation.goToAccess')}
          </Link>
        </div>
      ) : (
        <form
          className="card stack"
          onSubmit={(event) => void submit(event)}
          data-testid="recovery-form"
        >
          <h2 style={{ margin: 0 }}>{t('recovery.title')}</h2>
          <div className="field">
            <label htmlFor="recovery-email">{t('recovery.email')}</label>
            <input
              id="recovery-email"
              className="input"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              required
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </div>
          {error !== null ? (
            <div className="alert error" role="alert" data-testid="recovery-error">
              {error}
            </div>
          ) : null}
          {/* The one action with the accent, as the sign in's. */}
          <button type="submit" className="btn accent block" disabled={busy}>
            {t('recovery.submit')}
          </button>
          <Link className="btn secondary block" to="/login">
            {t('invitation.goToAccess')}
          </Link>
        </form>
      )}
    </div>
  );
}
